import type { JSX } from 'react';
import { FormSection } from '@ksyos/design-system';
import { useEffect } from 'react';
import type { BariPreReferralData } from '@common/validation/customControls/bariPre/bariPreReferral/v1/bariPreReferralSchemaV1';
import { useProcessActivityEditContext } from '@frontend/utils';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { AlertCheckbox } from '../../AlertCheckbox';
import { ProcessActivityAssetsDropzone } from '../../processActivityAssets/ProcessActivityAssetsDropzone';
import { ProcessActivityCareProviderSelector } from '../../ProcessActivityCareProviderSelector';
import { TextAreaFormGroup, TextInputFormGroup } from '../../processActivityFormGroupFields';

export function BariPreReferralEdit(): JSX.Element {
    const { data, errors, processActivityId, onChange } = useProcessActivityEditContext<BariPreReferralData>();
    const { patient } = usePatientEpisodeCategoryContext();

    useEffect(() => {
        if (!processActivityId && (patient.emailAddress || patient.homePhoneNumber || patient.mobileNumber)) {
            onChange({
                patient: {
                    emailAddress: data.patient.emailAddress || patient.emailAddress || null,
                    homePhoneNumber: data.patient.homePhoneNumber || patient.homePhoneNumber || null,
                    mobileNumber: data.patient.mobileNumber || patient.mobileNumber || null,
                },
            });
        }
    }, [processActivityId, patient.emailAddress, patient.homePhoneNumber, patient.mobileNumber]);

    return (
        <>
            <FormSection>
                <AlertCheckbox
                    checked={data.toestemming_van_patient}
                    hasError={errors.has('toestemming_van_patient')}
                    onChange={(checked) => onChange({ toestemming_van_patient: checked }, 'toestemming_van_patient')}
                    text="Patiënt geeft toestemming voor het verwerken van de persoons- en medische gegevens*"
                    title="Toestemming van de patiënt"
                    titleDanger="Geef alsjeblieft eerst aan of de patiënt toestemming geeft"
                />
            </FormSection>
            <FormSection title="Verwijsbrief">
                <ProcessActivityAssetsDropzone
                    errorText="Voeg alsjeblieft het bestand met de verwijsbrief toe"
                    isRequired={true}
                    label="Verwijsbrief"
                />
                <TextAreaFormGroup
                    fieldKey="toelichting"
                    label="Toelichting"
                    placeholder="Typ je toelichting of opmerking..."
                />
            </FormSection>
            <FormSection title="Medisch dossier nummer">
                <TextInputFormGroup
                    fieldKey="medisch_dossier_nummer"
                    isRequired={true}
                    label="MDN (Medisch Dossier Nummer)"
                />
            </FormSection>
            <FormSection title="Contactgegevens patiënt">
                <TextInputFormGroup
                    fieldKey="patient.homePhoneNumber"
                    label="Telefoonnummer patiënt"
                    placeholder="Telefoonnummer van de patiënt..."
                />
                <TextInputFormGroup
                    fieldKey="patient.mobileNumber"
                    isRequired={true}
                    label="Mobiel telefoonnummer patiënt"
                    placeholder="Telefoonnummer van de patiënt..."
                />
                <TextInputFormGroup
                    fieldKey="patient.emailAddress"
                    isRequired={true}
                    label="E-mailadres patiënt"
                    placeholder="E-mailadres van de patiënt..."
                />
            </FormSection>
            <FormSection title="Huisarts van de patiënt">
                <ProcessActivityCareProviderSelector
                    error={errors.get('generalPractitioner.practitionerId')}
                    generalPractitioner={data.generalPractitioner}
                    itemsPerPage={5}
                    onChange={(organizationId, practitionerId) => {
                        onChange(
                            {
                                generalPractitioner: {
                                    organizationId,
                                    practitionerId,
                                },
                            },
                            'generalPractitioner.practitionerId'
                        );
                    }}
                    patient={patient}
                />
            </FormSection>
        </>
    );
}
